import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GuestDashboard from '../pages/GuestDashboard';
import {
  LayoutDashboard,
  Key,
  FileText,
  Users,
  LogOut,
  Menu,
  X,
  Shield,
  Share2,
  Lock,
  Server,
  Download,
  Network,
  User,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Settings
} from 'lucide-react';
import './Layout.css';

interface NavItem {
  path?: string;
  icon: React.ComponentType<any>;
  label: string;
  children?: NavItem[];
}

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setProfileMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = (menuLabel: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuLabel)) {
        newSet.delete(menuLabel);
      } else {
        newSet.add(menuLabel);
      }
      return newSet;
    });
  };

  const navItems: NavItem[] = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/credentials', icon: Key, label: 'Credentials' },
    { path: '/generate-password', icon: Lock, label: 'Generate Password' },
    { path: '/kubeconfig', icon: Server, label: 'Kubeconfig' },
    { path: '/vpn', icon: Network, label: 'VPN' },
  ];

  // Only show Backup for admins
  if (user?.role === 'SUPER_ADMIN' || user?.role === 'IT_ADMIN') {
    navItems.push({ path: '/backup', icon: Download, label: 'Backup' });
  }

  // Only show Active Shares and Audit Logs for admins and auditors
  if (user?.role === 'SUPER_ADMIN' || user?.role === 'IT_ADMIN' || user?.role === 'AUDITOR') {
    navItems.push({ path: '/shares', icon: Share2, label: 'Active Shares' });
    navItems.push({ path: '/audit', icon: FileText, label: 'Audit Logs' });
  }

  // Only show Administration menu with sub-items for admins
  if (user?.role === 'SUPER_ADMIN' || user?.role === 'IT_ADMIN') {
    navItems.push({
      icon: Settings,
      label: 'Administration',
      children: [
        { path: '/users', icon: Users, label: 'Manage Users' },
        { path: '/mfa-management', icon: KeyRound, label: 'Manage MFA' },
      ],
    });
  }

  // Check if a menu item or its children is active
  const isItemActive = (item: NavItem): boolean => {
    if (item.path && location.pathname === item.path) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) => child.path === location.pathname);
    }
    return false;
  };

  // Auto-expand menu if a child is active
  React.useEffect(() => {
    const itemsToExpand: string[] = [];
    // Check all navigation items for active children
    const allNavItems: NavItem[] = [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/credentials', icon: Key, label: 'Credentials' },
      { path: '/generate-password', icon: Lock, label: 'Generate Password' },
      { path: '/kubeconfig', icon: Server, label: 'Kubeconfig' },
      { path: '/vpn', icon: Network, label: 'VPN' },
    ];

    if (user?.role === 'SUPER_ADMIN' || user?.role === 'IT_ADMIN') {
      allNavItems.push({ path: '/backup', icon: Download, label: 'Backup' });
      allNavItems.push({
        icon: Settings,
        label: 'Administration',
        children: [
          { path: '/users', icon: Users, label: 'Manage Users' },
          { path: '/mfa-management', icon: KeyRound, label: 'Manage MFA' },
        ],
      });
    }

    if (user?.role === 'SUPER_ADMIN' || user?.role === 'IT_ADMIN' || user?.role === 'AUDITOR') {
      allNavItems.push({ path: '/shares', icon: Share2, label: 'Active Shares' });
      allNavItems.push({ path: '/audit', icon: FileText, label: 'Audit Logs' });
    }

    allNavItems.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some((child) => child.path === location.pathname);
        if (hasActiveChild) {
          itemsToExpand.push(item.label);
        }
      }
    });

    if (itemsToExpand.length > 0) {
      setExpandedMenus((prev) => {
        const newSet = new Set(prev);
        itemsToExpand.forEach((label) => newSet.add(label));
        return newSet;
      });
    }
  }, [location.pathname, user?.role]);

  return (
    <div className="layout">
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <Shield size={24} />
            <span>Password Wallet</span>
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <X size={24} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedMenus.has(item.label);
            const isActive = isItemActive(item);

            if (hasChildren) {
              return (
                <div key={item.label} className="nav-group">
                  <button
                    className={`nav-item nav-parent ${isActive ? 'active' : ''}`}
                    onClick={() => toggleMenu(item.label)}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                    {isExpanded ? (
                      <ChevronDown size={16} className="chevron-icon" />
                    ) : (
                      <ChevronRight size={16} className="chevron-icon" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="nav-children">
                      {item.children!.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = location.pathname === child.path;
                        return (
                          <Link
                            key={child.path}
                            to={child.path!}
                            className={`nav-item nav-child ${isChildActive ? 'active' : ''}`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <ChildIcon size={18} />
                            <span>{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path!}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-name">{user?.username}</div>
            <div className="user-role">{user?.role.replace('_', ' ')}</div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="main-content">
        <div className="topbar">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu size={24} />
          </button>
          <div className="topbar-title">
            {(() => {
              // Check direct items first
              const directItem = navItems.find((item) => item.path === location.pathname);
              if (directItem) return directItem.label;

              // Check nested items
              for (const item of navItems) {
                if (item.children) {
                  const childItem = item.children.find((child) => child.path === location.pathname);
                  if (childItem) return childItem.label;
                }
              }

              return 'Password Wallet';
            })()}
          </div>
          <div className="topbar-actions">
            <div className="profile-menu" ref={profileMenuRef}>
              <button
                className={`profile-avatar ${location.pathname === '/profile' ? 'active' : ''}`}
                onClick={() => setProfileMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen}
                title="Account"
              >
                <div className="avatar-circle">
                  {user?.username ? (
                    <span className="avatar-initials">
                      {user.username.substring(0, 2).toUpperCase()}
                    </span>
                  ) : (
                    <User size={20} />
                  )}
                </div>
              </button>
              {profileMenuOpen && (
                <div className="profile-dropdown" role="menu">
                  <div className="profile-dropdown-header">
                    <div className="user-name">{user?.username}</div>
                    <div className="user-role">{user?.role?.replace('_', ' ')}</div>
                  </div>
                  <Link to="/profile" className="profile-dropdown-item" role="menuitem">
                    <User size={16} />
                    <span>My Profile</span>
                  </Link>
                  <button className="profile-dropdown-item profile-dropdown-item-danger" onClick={handleLogout} role="menuitem">
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="content">
          <div className="page-transition" key={location.pathname}>
            {user?.isGuest ? <GuestDashboard /> : <Outlet />}
          </div>
        </div>
      </div>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default Layout;

